import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { faSync, faHistory, faPrint } from '@fortawesome/free-solid-svg-icons';
import { PackageStatusUpdateDto, UmraPackageProperties, UmraPackagesService } from '../Services/umra-packages.service';
import { Subject, interval, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AgencyMasterProperties } from '../Models/AgencyMasterProperties';
import { HotelImage } from '../hotel-gallery/hotel-gallery.component';
import { BreakpointObserver } from '@angular/cdk/layout';

interface AirlineFilter { name: string; selected: boolean; count: number; }
interface RouteFilter { route: string; selected: boolean; count: number; }
interface DurationFilter { nights: number; label: string; selected: boolean; count: number; }
type SortOption = 'dateAsc' | 'dateDesc' | 'priceAsc' | 'priceDesc';

/** A package + values precomputed ONCE at load (search index, sort keys, logo URLs). */
export interface PackageView extends UmraPackageProperties {
  route: string;        // "routeStart → routeEnd"
  travelTime: number;   // travelDate as ms
  searchIndex: string;  // one lowercase blob of all searchable fields
  logoUrl: string;
  logoFallback: string;
}

const KIWI = 'https://images.kiwi.com/airlines/64/';
const AIRLINE_LOGOS: { [name: string]: string } = {
  'AirBlue': KIWI + 'PA.png',
  'PIA': KIWI + 'PK.png',
  'Pakistan International Airlines': KIWI + 'PK.png',
  'Saudi Arabian Airlines': KIWI + 'SV.png'
};

/* measured height of ONE rendered card, desktop / mobile.
   Must match the CSS heights set in the stylesheet. */
const CARD_HEIGHT_DESKTOP = 368;   // <-- measured desktop card height (px)
const CARD_HEIGHT_MOBILE = 1200;   // <-- measured mobile card height (px)
const CARD_GAP = 16;               // bootstrap mb-3 between cards

@Component({
  selector: 'app-umra-offers',
  templateUrl: './umra-offers.component.html',
  styleUrls: ['./umra-offers.component.css']
})
export class UmraOffersComponent implements OnInit, OnDestroy {
  faSync = faSync;
  faHistory = faHistory;
  faPrint = faPrint;

  allPackages: PackageView[] = [];
  filteredPackages: PackageView[] = [];

  searchControl = new FormControl('');
  selectedSort: SortOption = 'dateAsc';

  airlineFilters: AirlineFilter[] = [];
  routeFilters: RouteFilter[] = [];
  durationFilters: DurationFilter[] = [];

  loading = false;
  error: string | null = null;
  agencyOnline: AgencyMasterProperties;

  // Cached values the template reads as plain fields (no method calls in HTML)
  activeFilterCount = 0;
  anyFilterActive = false;

  // Virtual scroll config
  itemSize = CARD_HEIGHT_DESKTOP + CARD_GAP;

  // PDF flyer only exists in the DOM while a flyer is being generated
  renderPdfTemplate = false;
  printLoading: boolean;

  // Summary strings passed to the flyer header
  selectedAirline = 'All Airlines';
  selectedRoute = 'All Routes';

  showRefreshModal = false;
  refreshModalClosable = false;

  showHotelGallery = false;
  galleryHotelName = '';
  galleryHotelType = '';
  galleryImages: HotelImage[] = [];

  private destroy$ = new Subject<void>();
  private timerSubscription: Subscription;

  constructor(private packageService: UmraPackagesService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    breakpoints: BreakpointObserver) {
    breakpoints.observe('(max-width: 991.98px)')
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.itemSize = (state.matches ? CARD_HEIGHT_MOBILE : CARD_HEIGHT_DESKTOP) + CARD_GAP;
      });
  }

  ngOnInit(): void {
    this.getOnlineAgency();
    this.loadAllPackages();
    this.setupSearchListener();
    this.timerSubscription = interval(1200000).subscribe(() => this.showRefreshPopup());
  }

  ngOnDestroy(): void {
    if (this.timerSubscription) { this.timerSubscription.unsubscribe(); }
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByPackageId(index: number, item: PackageView): number {
    return item.packageId;
  }

  showRefreshPopup(): void { this.showRefreshModal = true; }

  getOnlineAgency(): void {
    this.packageService.getOnlineAgency(localStorage.getItem('AgencyId')).subscribe({
      next: (response) => {
        if (response.success) {
          this.agencyOnline = response.agency;
        } else {
          this.error = response.message || 'Failed to fetch online agency';
        }
      },
      error: (error) => {
        console.error('Error fetching online agency:', error);
        this.error = 'Failed to fetch online agency.';
      }
    });
  }

  loadAllPackages(): void {
    this.loading = true;
    this.error = null;
    this.packageService.getAllPackages().subscribe({
      next: (response) => {
        if (response.success) {
          this.allPackages = this.preparePackages(response.umraPackagesList);
          this.initializeFilters();
          this.applyAllFilters();
        } else {
          this.error = response.message || 'Failed to load packages';
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading packages:', error);
        this.error = 'Failed to load packages. Please try again.';
        this.loading = false;
      }
    });
  }

  private setupSearchListener(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.applyAllFilters());
  }

  private preparePackages(list: UmraPackageProperties[]): PackageView[] {
    return list.map(pkg => ({
      ...pkg,
      route: (pkg.routeStart && pkg.routeEnd) ? `${pkg.routeStart} → ${pkg.routeEnd}` : '',
      travelTime: Date.parse(pkg.travelDate) || 0,
      searchIndex: [
        pkg.packageTitle, pkg.makkahHotel, pkg.madinahHotel, pkg.routeStart,
        pkg.routeEnd, pkg.airlineName, pkg.badgeText, pkg.provider,
        pkg.groupCode, pkg.packageCode, pkg.customPackageId
      ].filter(Boolean).join(' ').toLowerCase(),
      logoUrl: pkg.airlineName ? `../../assets/hotels/${pkg.airlineName}.png` : '',
      logoFallback: AIRLINE_LOGOS[pkg.airlineName] || (KIWI + 'airlines.png')
    }));
  }

  private initializeFilters(): void {
    const airlineMap = new Map<string, number>();
    const routeMap = new Map<string, number>();
    const durationMap = new Map<number, number>();

    for (const pkg of this.allPackages) {
      if (pkg.airlineName) { airlineMap.set(pkg.airlineName, (airlineMap.get(pkg.airlineName) || 0) + 1); }
      if (pkg.route) { routeMap.set(pkg.route, (routeMap.get(pkg.route) || 0) + 1); }
      if (pkg.nightsCount) { durationMap.set(pkg.nightsCount, (durationMap.get(pkg.nightsCount) || 0) + 1); }
    }

    this.airlineFilters = Array.from(airlineMap.entries())
      .map(([name, count]) => ({ name, selected: false, count }))
      .sort((a, b) => a.name.localeCompare(b.name));

    this.routeFilters = Array.from(routeMap.entries())
      .map(([route, count]) => ({ route, selected: false, count }))
      .sort((a, b) => a.route.localeCompare(b.route));

    this.durationFilters = Array.from(durationMap.entries())
      .map(([nights, count]) => ({ nights, label: `${nights} Nights`, selected: false, count }))
      .sort((a, b) => a.nights - b.nights);
  }

  /** Single pass with Sets – ~1–2 ms for 2000 packages. */
  private applyAllFilters(): void {
    const q = (this.searchControl.value || '').trim().toLowerCase();

    const airlines = new Set<string>();
    const routes = new Set<string>();
    const nights = new Set<number>();
    for (const f of this.airlineFilters) { if (f.selected) { airlines.add(f.name); } }
    for (const f of this.routeFilters) { if (f.selected) { routes.add(f.route); } }
    for (const f of this.durationFilters) { if (f.selected) { nights.add(f.nights); } }

    const out: PackageView[] = [];
    for (const p of this.allPackages) {
      if (q && p.searchIndex.indexOf(q) === -1) { continue; }
      if (airlines.size && !airlines.has(p.airlineName)) { continue; }
      if (routes.size && !routes.has(p.route)) { continue; }
      if (nights.size && !nights.has(p.nightsCount)) { continue; }
      out.push(p);
    }

    switch (this.selectedSort) {
      case 'dateDesc': out.sort((a, b) => b.travelTime - a.travelTime); break;
      case 'priceAsc': out.sort((a, b) => a.priceSharing - b.priceSharing); break;
      case 'priceDesc': out.sort((a, b) => b.priceSharing - a.priceSharing); break;
      default: out.sort((a, b) => a.travelTime - b.travelTime); break;
    }

    this.filteredPackages = out;

    // cache what the template used to compute via method calls
    this.activeFilterCount = (q ? 1 : 0) + airlines.size + routes.size + nights.size;
    this.anyFilterActive = this.activeFilterCount > 0 || this.selectedSort !== 'dateAsc';
    this.selectedAirline = airlines.size ? Array.from(airlines).join(', ') : 'All Airlines';
    this.selectedRoute = routes.size ? Array.from(routes).join(', ') : 'All Routes';
  }

  // ---------- filter UI handlers ----------
  toggleAirlineFilter(airlineName: string): void {
    const filter = this.airlineFilters.find(f => f.name === airlineName);
    if (filter) { filter.selected = !filter.selected; this.applyAllFilters(); }
  }

  toggleRouteFilter(route: string): void {
    const filter = this.routeFilters.find(f => f.route === route);
    if (filter) { filter.selected = !filter.selected; this.applyAllFilters(); }
  }

  toggleDurationFilter(nights: number): void {
    const filter = this.durationFilters.find(f => f.nights === nights);
    if (filter) { filter.selected = !filter.selected; this.applyAllFilters(); }
  }

  get allAirlinesSelected(): boolean {
    return this.airlineFilters.length === 0 || !this.airlineFilters.some(f => f.selected);
  }
  get allRoutesSelected(): boolean {
    return this.routeFilters.length === 0 || !this.routeFilters.some(f => f.selected);
  }
  get allDurationsSelected(): boolean {
    return this.durationFilters.length === 0 || !this.durationFilters.some(f => f.selected);
  }

  toggleAllAirlines(): void {
    if (!this.allAirlinesSelected) { this.airlineFilters.forEach(f => f.selected = false); this.applyAllFilters(); }
  }
  toggleAllRoutes(): void {
    if (!this.allRoutesSelected) { this.routeFilters.forEach(f => f.selected = false); this.applyAllFilters(); }
  }
  toggleAllDurations(): void {
    if (!this.allDurationsSelected) { this.durationFilters.forEach(f => f.selected = false); this.applyAllFilters(); }
  }

  onAirlineFilterChange(): void { this.applyAllFilters(); }
  onRouteFilterChange(): void { this.applyAllFilters(); }
  onNightsFilterChange(): void { this.applyAllFilters(); }

  onSortChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.selectedSort = select.value as SortOption;
    this.applyAllFilters();
  }

  resetAllFilters(): void {
    this.searchControl.setValue('', { emitEvent: false });
    this.selectedSort = 'dateAsc';
    this.airlineFilters.forEach(f => f.selected = false);
    this.routeFilters.forEach(f => f.selected = false);
    this.durationFilters.forEach(f => f.selected = false);
    this.applyAllFilters();
  }

  toMyRequests(): void { this.router.navigateByUrl('/my-umra-reservation-requests'); }

  syncFromApi(): void {
    this.loading = true;
    this.packageService.syncNow().subscribe({
      next: (response) => {
        if (response.success) {
          this.loadAllPackages();
        } else {
          this.error = response.message || 'Sync failed';
          this.loading = false;
        }
        this.refreshModalClosable = true;
        this.showRefreshModal = false;
      },
      error: (error) => {
        this.error = 'Sync failed: ' + error;
        this.refreshModalClosable = true;
        this.showRefreshModal = false;
        this.loading = false;
      }
    });
  }

  togglePackageStatus(packageStatus: UmraPackageProperties): void {
    const statusUpdate: PackageStatusUpdateDto = { isActive: !packageStatus.isActive };
    this.packageService.updatePackageStatus(packageStatus.packageId, statusUpdate).subscribe({
      next: (response) => {
        if (response.success) { packageStatus.isActive = !packageStatus.isActive; }
      },
      error: (error) => { console.error('Failed to update status:', error); }
    });
  }

  addNewPackage(newPackage: UmraPackageProperties): void {
    this.packageService.insertOrUpdateSinglePackage(newPackage).subscribe({
      next: (response) => { if (response.success) { this.loadAllPackages(); } },
      error: (error) => { console.error('Failed to add package:', error); }
    });
  }

  addNewPackageBatch(newPackages: UmraPackageProperties[]): void {
    this.packageService.insertOrUpdatePackageBatch(newPackages).subscribe({
      next: (response) => { if (response.success) { this.loadAllPackages(); } },
      error: (error) => { console.error('Failed to add packages:', error); }
    });
  }

  /* Flyer: the template is mounted ONLY while generating. */
  async printPackages(): Promise<void> {
    if (this.printLoading) { return; }
    this.printLoading = true;
    this.error = null;
    this.renderPdfTemplate = true;              // <-- mount the flyer now
    this.cdr.detectChanges();
    try {
      // gave Angular time to render it (need to increase 500 if a big flyer comes out cut off)
      await new Promise(resolve => setTimeout(resolve, 500));

      const dates = this.filteredPackages.map(p => new Date(p.travelDate));
      const firstDate = dates.length > 0
        ? new Date(Math.min(...dates.map(d => d.getTime()))).toISOString().split('T')[0]
        : 'all';
      const today = new Date().toISOString().split('T')[0];
      const filename = `Tushmit_Travels_Umrah_Packages_Flyer_${firstDate}_${today}.pdf`;

      await this.packageService.generatePdf('pdfContent', filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      this.error = 'Failed to generate PDF. Please try again.';
    } finally {
      this.printLoading = false;
      this.renderPdfTemplate = false;           // <-- unmount it again
      this.cdr.detectChanges();
    }
  }

  // ---------- hotel gallery ----------
  openHotelGallery(hotelName: string, hotelType: string): void {
    this.galleryHotelName = hotelName;
    this.galleryHotelType = hotelType;
    this.galleryImages = this.buildHotelImages(hotelName, hotelName);
    this.showHotelGallery = true;
  }

  closeHotelGallery(): void {
    this.showHotelGallery = false;
    this.galleryImages = [];
  }

  private buildHotelImages(folderName: string, hotelName: string): HotelImage[] {
    const imageCount = 15;
    const images: HotelImage[] = [];
    for (let i = 1; i <= imageCount; i++) {
      images.push({
        imageUrl: `assets/hotels/${folderName}/hotel-image-${i}.jpg`,
        caption: `${hotelName} - View ${i}`
      });
    }
    return images;
  }
}
